import React from 'react';
import { Button, message } from 'antd';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import useCurrentUser from 'hooks/useCurrentUser';
import NewMessagePanel from 'components/NewMessagePanel';
import { gpSendMessage } from 'api/group';
import { sendMessage } from 'api/inbox';
import { getEligibilitySkipCostModal, getUserId, setEligibilitySkipCostModal } from 'utils/localstore';
import {
    ELIGIBILITY_ADVANCED_PRICE_CENTS,
    ELIGIBILITY_PRICE_CENTS,
    TPayerCandidate,
    TProviderNotificationItemVo,
} from 'types/insuranceEligibility';
import {
    ADV_LOADING_COPY,
    CARD_REQUEST_COPY,
    GAP_COPY,
    INFO_COPY,
    PLAN_SCOPE_LINE,
    SET_DEFAULT_COPY,
    LOADING_COPY,
    MSG_OWNER_COPY,
    NPI_MODAL_COPY,
    RECHECK_COPY,
    ROUTE_MODAL_COPY,
    SECOND_FAIL_FIX,
    TEMPLATE_GAP_COPY,
} from './copy';
import { TAaaEntry, isFailureResult } from './aaa';
import { isEligibleResult } from './derive';
import { ERoleCode } from 'types/enumItems';
import { PATH } from 'constants/path';
import { getNotificationItems } from 'api/eligibility';
import { withZone } from 'api/eligibility/adapt';
import { buildPatientDraft, buildRouteConfirmDraft } from './messageDrafts';
import CostConfirmModal from './components/CostConfirmModal';
import CardRequestModal from './components/CardRequestModal';
import RouteConfirmModal from './components/RouteConfirmModal';
import OutreachStrip from './components/OutreachStrip';
import NpiSetupModal from './components/NpiSetupModal';
import MessageOwnerModal from './components/MessageOwnerModal';
import {
    CardUpdateNote,
    ChooseProviderNote,
    PracticeGapNote,
    TeamRequestBanner,
    TemplateGapNote,
} from './components/GapNotes';
import UnmappedPayerCard from './components/UnmappedPayerCard';
import IdleCard from './components/IdleCard';
import PickInsuranceCard from './components/PickInsuranceCard';
import OtherPrimaryOffer from './components/OtherPrimaryOffer';
import ResultCard from './components/ResultCard';
import HistoryModal from './components/HistoryModal';
import type { TEligibilityApi } from './hooks/useClientEligibilityCheck';
import s from './s.module.less';

// managed 每日手动 re-check 上限（per patient per day）；剩余次数永不展示
const DAILY_MANUAL_RECHECK_LIMIT = 5;

type TPersona = 'managed' | 'manual';

type InnerProps = {
    api: TEligibilityApi;
    persona: TPersona;
};

const EligibilityModuleInner: React.FC<InnerProps> = ({ api, persona }) => {
    const [currentUser] = useCurrentUser();
    const [costModal, setCostModal] = React.useState<'check' | 'advanced' | 'other' | null>(null);
    // cost modal 点名的那张：取这次实际发往的 id（未指定时 = hook 的 checkInsurance），不读结果卡的 carrier 快照，
    // 也不读「查另一张」时的 checkInsurance——前者可能是另一张（test 10-22：次保险结果卡 Re-check），
    // 后者在详情重拉时会被清回结果卡那张（test 10-21：选 Cigna 弹窗写 Aetna）
    const [costTargetId, setCostTargetId] = React.useState<number>();
    // 这次 check 发往哪张保险（跨 cost modal / 设置弹窗保留）：Re-check 指定结果卡那张、「查另一张」指定选中那张；
    // undefined = hook 的 checkInsurance。用 ref 而非闭包——Don't ask again 时同一次点击里直接跑
    const pendingTargetRef = React.useRef<{ id?: number; announce?: boolean }>({});
    const [skipCostModal, setSkipCostModal] = React.useState(getEligibilitySkipCostModal());
    const [msgOpen, setMsgOpen] = React.useState(false);
    const [msgDraft, setMsgDraft] = React.useState('');
    const [cardSending, setCardSending] = React.useState(false);
    const [routeSending, setRouteSending] = React.useState(false);
    // payer ID / NPI 设置弹窗：blocked = check 流触发；review = 横幅进入（仅 payer 步）
    const [setupModal, setSetupModal] = React.useState<'blocked' | 'review' | null>(null);
    // review 模式的锚点：team request 横幅传通知里的 providerId；payer gap 横幅不传（弹窗锚定 self）
    const [reviewAnchorProviderId, setReviewAnchorProviderId] = React.useState<number>();
    const [msgOwnerOpen, setMsgOwnerOpen] = React.useState(false);
    // owner 的 GROUP_MEMBER_PAYER_ID_SET_UP 未读通知（横幅 + grid chip 圆点用；items 只回未读）
    const [teamItems, setTeamItems] = React.useState<TProviderNotificationItemVo[]>([]);

    // group 角色：owner 管 practice 级设置；member 对 billing NPI 只读、payer ID 可自填自己的。
    // ERoleCode 只有 GROUP_* 值，solo 无 roleCode——roleCode 本身即可判三态；
    // 不用 groupPractice 标志（它随 practice/my 视图切换变化，角色不能跟着视图漂移）
    let role: 'owner' | 'member' | 'solo' = 'solo';
    if (currentUser?.roleCode === ERoleCode.GROUP_OWNER) role = 'owner';
    else if (currentUser?.roleCode) role = 'member';

    const { prospect } = api;
    const patientName = `${prospect?.firstName || ''} ${prospect?.lastName || ''}`.trim();
    const patientFirst = prospect?.firstName || 'there';
    // 结果卡那张的 member ID（修复建议 / 消息草稿点名的号码）；那张已不在档案上时用下一次 check 发往的那张。
    // 取当前 provider 的 sibling（activeInsurances），不是 identity 顶层，也不是按 sort 的第一张
    const memberIdOnFile = (api.activeInsurances?.find((ins) => ins.id === api.cardInsuranceId) || api.checkInsurance)
        ?.memberId;

    // ── check 发出前的设置门槛（原型 afterCostApproved：批钱和集料是两道不同的闸）──
    // 缺 billing NPI（practice 级）或本患者 carrier 的 payer ID（provider 级）时，
    // cost modal 之后先弹设置弹窗——先同意花钱，再做设置工作，顺序不可反。
    // 设置闸对两种 persona 一视同仁（enrolled 也要 setup，2026-09-15 拍板）；enrolled 的豁免只有 cost modal。
    // 全部 hook 必须在下面的早退 return 之前（stateVo 未就绪时用可选链空转）
    const setup = api.stateVo?.setup;
    const needNpi = !!setup && !setup.billingNpiSet;
    const payerMissing = setup?.payerIdMissing;
    // carrier 不在 service template 上：与 payerIdMissing 互斥、优先级更高。设置弹窗对它无能为力
    // （grid 无此行），所以它不进 needsSetup——check 入口直接被横幅取代/拦 toast
    const templateMissing = setup?.templateMissing;
    const needsSetup = needNpi || !!payerMissing;
    // practice 视图下 template 归被查的那位 provider，当前登录者改不了 → 只说事实不给按钮
    const canAddToTemplate = !api.isGroupPractice;
    // 这一条不看 NPI：billing NPI 补齐了 check 照样发不出去，闸要一直关着。
    // 横幅只显示一条，member 的 NPI 条排在前（那才是他这一刻真正等的人）
    const templateGap = !!templateMissing;
    const openServiceTemplate = () => window.open(`/${PATH.PPA_SERVICE_TEMPLATE}?from=menu`, '_blank');

    // owner 的 team request 横幅：GROUP_MEMBER_PAYER_ID_SET_UP 未读通知驱动（新 member 首次保存
    // template 触发）。payer-config 保存成功后后端自动按 provider 置读，这里只需重查。
    // enrolled 的 owner 同样要处理 member 的 payer ID 请求，不按 persona 过滤
    const loadTeamRequests = React.useCallback(async () => {
        if (role !== 'owner') return;
        const items = await getNotificationItems('GROUP_MEMBER_PAYER_ID_SET_UP');
        setTeamItems(items);
    }, [role]);

    React.useEffect(() => {
        loadTeamRequests();
    }, [loadTeamRequests]);

    // 通知涉及的 provider（chip 圆点 + 横幅计数按 providerId 去重）
    const teamPendingProviderIds = React.useMemo(
        () =>
            Array.from(
                new Set(teamItems.map((it) => Number(it.metaData?.providerId)).filter((id) => Number.isFinite(id))),
            ),
        [teamItems],
    );

    // payer gap 横幅（payerIdMapped=false）：owner/member 都显示，形态按角色分（2026-09-10 拍板）
    const memberPayerGap = role === 'member' && !needNpi && !!payerMissing;
    const ownerPayerGap = role !== 'member' && !needNpi && !!payerMissing;

    if (!api.enabled || !prospect) {
        return null;
    }

    // Request updated insurance 有 prosepectInfo 的常驻入口（原型 #insActions），不依赖失败状态、
    // 无保险的患者也能主动要卡——弹窗必须先于下面的早退渲染，否则常驻按钮点了没反应
    const handleSendCardRequest = async (note: string, channels: Array<'chat' | 'text' | 'email'>) => {
        setCardSending(true);
        try {
            await api.sendCardRequest(note, channels);
            message.success(channels.length ? CARD_REQUEST_COPY.sent : CARD_REQUEST_COPY.sentNoChannel);
        } finally {
            setCardSending(false);
        }
    };

    // 无码入口（顶部常驻按钮 / inactive 卡）的预填语境：结果卡那张没查成功（不是 Eligible 都算，
    // UNMAPPED 也算）→ 点名那张 "{Carrier} {Plan}" 要新照片，多份 primary 时病人才知道重传哪一张；
    // 没查过 / 查成功 → 中性请求，不预设一次不存在的失败
    const unmatchedOnly = !api.latestCheck && !!api.payerUnmatched;
    const cardFailed = unmatchedOnly || (!!api.latestCheck && !isEligibleResult(api.latestCheck));
    const failedInsuranceId = unmatchedOnly
        ? (api.payerUnmatched?.insuranceId ?? api.cardInsuranceId)
        : api.cardInsuranceId;
    const failedInsurance = api.activeInsurances?.find((ins) => ins.id === failedInsuranceId);
    const cardRequestContext = cardFailed ? ('failedPlan' as const) : ('neutral' as const);
    const failedPlanName = [
        failedInsurance?.carrier || api.latestCheck?.carrier,
        failedInsurance?.plan || api.latestCheck?.plan,
    ]
        .filter(Boolean)
        .join(' ');

    // History 弹窗与 Request card 同处理：入口在标题行常驻，弹窗必须在早退之前构造，
    // 否则无保险/无 stateVo 时点了没反应
    const historyModal = (
        <HistoryModal
            open={api.historyOpen}
            loading={api.historyLoading}
            loadingMore={api.historyLoadingMore}
            rows={api.historyRows}
            hasMore={api.historyHasMore}
            // 结果卡上那次在流水里标 Active（后端无此标记，按时间戳对齐）
            currentCheckedAt={api.latestCheck?.checkedAt}
            // solo 只有一个人在按按钮，Checked by 整列没有信息量
            showWho={!!api.isGroupPractice}
            onLoadMore={api.loadMoreHistory}
            onClose={api.closeHistory}
        />
    );

    const cardRequestModal = (
        <CardRequestModal
            open={api.cardRequestOpen}
            payerName={api.carrier}
            aaa={api.cardRequestAaa}
            context={cardRequestContext}
            planName={failedPlanName || undefined}
            persona={persona}
            sending={cardSending}
            onCancel={api.closeCardRequest}
            onSend={handleSendCardRequest}
        />
    );

    // practice 视图、谁都没被选中（这个患者从未被查过）：整个模块体换成「先挑一位」提示条。
    // 没有选中的 provider 就没有 clientId / insuranceId，下面的一切都无从谈起
    if (api.providers.length > 0 && !api.selectedProviderId) {
        return (
            <div className={s.module}>
                <ChooseProviderNote />
                {cardRequestModal}
                {historyModal}
            </div>
        );
    }

    if (!api.hasInsurance || !api.stateVo) {
        // 无保险：模块体不渲染（header 的 No insurance tag 承载全部信息），但常驻入口的弹窗保留
        return (
            <>
                {cardRequestModal}
                {historyModal}
            </>
        );
    }

    // ── 真实发消息（solo 走 inbox sendMessage，group 走 gpSendMessage）──
    const sendPatientMessage = async (text: string): Promise<boolean> => {
        const msg = text.replace(/<[^>]+>/g, '').trim();
        if (!msg) return false;
        if (currentUser?.groupPractice) {
            const res: any = await gpSendMessage({
                recipientType: api.isGroupPractice && prospect.identityId ? 'GP_PROSPECT_IDENTITY' : 'PATIENT',
                targetId: (api.isGroupPractice && prospect.identityId ? prospect.identityId : prospect.id)!,
                message: msg,
            });
            return !res?.error;
        }
        const res: any = await sendMessage({ providerId: getUserId()!, clientId: prospect.id!, message: msg });
        return !res?.error;
    };

    const proceedAfterCost = () => {
        if (needsSetup) {
            // billing NPI 只有 owner/solo 能设；member 到这一步只能等 owner
            if (role === 'member' && needNpi) {
                message.info(GAP_COPY.npiMemberToast);
                return;
            }
            setSetupModal('blocked');
            return;
        }
        api.run(pendingTargetRef.current.id, { announce: pendingTargetRef.current.announce });
    };

    // manual persona 每次计费动作都要过 cost modal（除非 Don't ask again）；managed（enrolled）
    // check 和 advanced search 都免费（2026-09-16 拍板，后端同步停收）——两处都不弹 cost modal，
    // 设置闸照走（enrolled 也要 setup）
    const startCheck = (opts?: { target?: number; other?: boolean }) => {
        pendingTargetRef.current = { id: opts?.target, announce: opts?.other };
        // carrier 不在 service template 上：设置弹窗补不了这一格，先别让人花钱走一遍
        if (templateGap) {
            message.info(canAddToTemplate ? TEMPLATE_GAP_COPY.toast : TEMPLATE_GAP_COPY.toastOther);
            return;
        }
        if (persona === 'manual' && !skipCostModal) {
            setCostTargetId(opts?.target ?? api.checkInsurance?.id);
            setCostModal(opts?.other ? 'other' : 'check');
            return;
        }
        proceedAfterCost();
    };

    const startAdvanced = () => {
        if (persona === 'manual') {
            setCostModal('advanced');
            return;
        }
        api.runAdvancedSearch();
    };

    const confirmCost = (dontAskAgain: boolean) => {
        const mode = costModal;
        setCostModal(null);
        if (mode === 'advanced') {
            api.runAdvancedSearch();
            return;
        }
        if (dontAskAgain) {
            setEligibilitySkipCostModal();
            setSkipCostModal(true);
        }
        proceedAfterCost();
    };

    const handleSetupComplete = async () => {
        const wasBlocked = setupModal === 'blocked';
        setSetupModal(null);
        await api.refreshState();
        loadTeamRequests();
        // blocked 路径：设置本来就是这次 check 的一部分，补齐即跑（末步按钮就叫 Run check）
        if (wasBlocked) api.run(pendingTargetRef.current.id, { announce: pendingTargetRef.current.announce });
    };

    // ── 二次失败（仅 managed；SOP scope 就是 billing program 内的 provider）──
    // 后端下发 outreach.stillFailing = Klarity 自动轮（更宽搜索→患者自纠→复查）跑完仍失败；
    // 三处联动：结果卡上方蓝 strip、修复卡 Recommended fix 换文案、Message patient 草稿整体换版
    const { outreach } = api.stateVo;
    const secondFailure = persona === 'managed' && !!outreach?.stillFailing && isFailureResult(api.latestCheck);
    let secondFailFix: string | undefined;
    if (secondFailure) {
        secondFailFix = outreach?.cashPay ? SECOND_FAIL_FIX.cash : SECOND_FAIL_FIX.noCash;
    }

    const openMessagePatient = (aaa?: TAaaEntry) => {
        setMsgDraft(
            buildPatientDraft({
                patientFirstName: patientFirst,
                payerName: api.carrier,
                memberIdOnFile,
                aaa,
                latestCheck: api.latestCheck,
                // 患者侧草稿不在本期需求：cash 金额/visit 时间后端未下发，草稿走无金额的通用版
                secondFailure: secondFailure ? {} : undefined,
            }),
        );
        setMsgOpen(true);
    };

    const handleRouteSend = async (text: string) => {
        setRouteSending(true);
        try {
            const ok = await sendPatientMessage(text);
            if (ok) message.success(ROUTE_MODAL_COPY.sent);
            // 发送即流程结束（不触发 check，UNMAPPED 保持）；unmapped 卡记录「已选 X · 已通知患者」。
            // 失败也不拦：动作已完成，患者稍后可从 Messages 补发
            api.confirmRouteNotified();
        } finally {
            setRouteSending(false);
        }
    };

    const latest = api.latestCheck;
    // To-do 带来的 recheck 不占限额：那一次点击放行，用掉后回落手动 check 再按上限拦
    const capReached =
        persona === 'managed' && !api.recheckPending && api.runsThisSession >= DAILY_MANUAL_RECHECK_LIMIT;
    let recheckTip: string | undefined;
    if (capReached) {
        recheckTip = RECHECK_COPY.managedLimitTip;
    } else if (persona === 'manual') {
        recheckTip = RECHECK_COPY.manualTip;
    }

    // ── 多份 primary（10/05）：结果卡上的附加块 ──
    // 不是 Eligible 都算失败 → 「查另一张」（仅 not enrolled；enrolled 由系统查，不支持选择——2026-10-12 拍板）；
    // Eligible 落在非 default 那张 → Set as default（两种 persona：enrolled 也能在首查时选非 default）
    // 结果卡答的是哪张：本地刚查完的那次带着 sendId，详情重拉前 cardInsuranceId 还是上一次的
    const shownInsuranceId = latest?.insuranceId ?? api.cardInsuranceId;
    const otherPrimaries = api.primaryInsurances.filter((ins) => ins.id !== shownInsuranceId);
    // 10/21：次保险也进「查另一张」，随时可选
    const otherSecondaries = api.secondaryInsurances.filter((ins) => ins.id !== shownInsuranceId);
    const cardIsPrimary = api.primaryInsurances.some((ins) => ins.id === api.cardInsuranceId);
    const shownInsurance = (api.activeInsurances || []).find((ins) => ins.id === shownInsuranceId);
    const shownIsSecondary = api.secondaryInsurances.some((ins) => ins.id === shownInsuranceId);
    // Last checked 下一行点名这次答的是哪张、主还是次：一律显示（10/22：多张主保险时也要知道是哪张；
    // 原型只在有次保险时出）。那张已从档案删掉时找不到槽位，不出
    const planScope = shownInsurance
        ? PLAN_SCOPE_LINE(shownInsurance.carrier || latest?.carrier || api.carrier, shownIsSecondary)
        : undefined;
    let resultExtra: React.ReactNode;
    let setDefaultNode: React.ReactNode;
    if (
        latest &&
        persona === 'manual' &&
        otherPrimaries.length + otherSecondaries.length > 0 &&
        !isEligibleResult(latest)
    ) {
        resultExtra = (
            <OtherPrimaryOffer
                insurances={otherPrimaries}
                secondaries={otherSecondaries}
                checkCards={api.checkCards}
                selectedId={api.checkInsurance?.id}
                onSelect={api.selectInsurance}
                onCheck={(id) => startCheck({ target: id, other: true })}
            />
        );
    } else if (
        latest &&
        api.cardInsuranceId != null &&
        api.defaultInsurance &&
        api.cardInsuranceId !== api.defaultInsurance.id &&
        cardIsPrimary &&
        isEligibleResult(latest)
    ) {
        setDefaultNode = (
            <div className={s.setDefaultRow}>
                <div>{SET_DEFAULT_COPY.line}</div>
                <div>
                    <Button loading={api.settingDefault} onClick={() => api.setDefault()}>
                        {SET_DEFAULT_COPY.cta}
                    </Button>
                </div>
            </div>
        );
    }

    // ── 模块主体 ──
    let bodyNode: React.ReactNode;
    if (api.checking || api.advancedLoading) {
        // loading：check / 目录搜索两套文案
        bodyNode = (
            <div className={s.loadingCard}>
                <span className={s.eligSpin} />
                <span>{api.advancedLoading ? ADV_LOADING_COPY : LOADING_COPY}</span>
            </div>
        );
    } else if (!latest && api.payerUnmatched) {
        // FR4：请求被挡在发出之前（advanced search 两态卡；选定候选只发患者通知，不重跑 check）。
        // UNMAPPED 也算失败：档案上还有别的 primary 时「查另一张」接在 advanced search 之后（仅 not enrolled）
        const unmatchedOthers = api.primaryInsurances.filter((ins) => ins.id !== failedInsuranceId);
        const unmatchedSecondaries = api.secondaryInsurances.filter((ins) => ins.id !== failedInsuranceId);
        const unmatchedIsSecondary = api.secondaryInsurances.some((ins) => ins.id === failedInsuranceId);
        bodyNode = (
            <UnmappedPayerCard
                carrierOnFile={api.payerUnmatched.carrierOnFile || api.carrier}
                planScope={
                    failedInsurance
                        ? PLAN_SCOPE_LINE(
                              failedInsurance.carrier || api.payerUnmatched.carrierOnFile || api.carrier,
                              unmatchedIsSecondary,
                          )
                        : undefined
                }
                persona={persona}
                candidates={api.advancedCandidates}
                routedNotice={api.routedNotice}
                foldOpen={api.foldOpen}
                onToggleFold={api.toggleFold}
                onRunAdvanced={startAdvanced}
                onPick={(c: TPayerCandidate) => api.pickPayer(c)}
                extra={
                    persona === 'manual' && unmatchedOthers.length + unmatchedSecondaries.length > 0 ? (
                        <OtherPrimaryOffer
                            insurances={unmatchedOthers}
                            secondaries={unmatchedSecondaries}
                            checkCards={api.checkCards}
                            selectedId={api.checkInsurance?.id}
                            onSelect={api.selectInsurance}
                            onCheck={(id) => startCheck({ target: id, other: true })}
                        />
                    ) : undefined
                }
            />
        );
    } else if (latest) {
        bodyNode = (
            <>
                {secondFailure && <OutreachStrip hasCashPrice={outreach?.cashPay === true} />}
                <ResultCard
                    check={latest}
                    carrier={api.carrier}
                    persona={persona}
                    skipCostModal={skipCostModal}
                    foldOpen={api.foldOpen}
                    onToggleFold={api.toggleFold}
                    // Re-check 查的是结果卡那张，不是档案上的 default（2026-10-12 规则）；改成 secondary 也照查它，
                    // 只有被删了才不指定，交给 hook 的 checkInsurance 回到 default primary
                    recheck={{
                        visible: true,
                        disabled: capReached,
                        tip: recheckTip,
                        onClick: () => startCheck({ target: shownInsurance ? shownInsuranceId : undefined }),
                    }}
                    clientId={api.clientId || 0}
                    memberIdOnFile={memberIdOnFile}
                    cardRequestedAt={api.cardRequestedAt}
                    secondFailFix={secondFailFix}
                    onRequestCard={(aaa) => api.openCardRequest(aaa)}
                    onMessagePatient={openMessagePatient}
                    templateWhose={canAddToTemplate ? INFO_COPY.planTypeWhoseSelf : INFO_COPY.planTypeWhoseOther}
                    onAddToTemplate={canAddToTemplate ? openServiceTemplate : undefined}
                    planScope={planScope}
                    setDefault={setDefaultNode}
                    extra={resultExtra}
                />
            </>
        );
    } else if (role === 'member' && needNpi) {
        // member 的 idle：billing NPI 未设时横幅取代 Check 按钮（横幅在 notes 区渲染，这里不出 CTA）
        bodyNode = null;
    } else if (
        api.needsInsurancePick &&
        !(templateGap && api.primaryInsurances.length + api.secondaryInsurances.length === 1)
    ) {
        // 从没查过：一律先选一张再 check（10/22 起只有一张也出，两种 persona）。排在 template gap 之前——
        // gap 按选中的那张算，选到不在 template 上的那张时横幅出现在上方，卡片必须还在，否则切不回去；
        // 只有一张时没得切换，仍由 gap 横幅取代（原型：横幅替代 Check 按钮）
        bodyNode = (
            <PickInsuranceCard
                persona={persona}
                hasUpcomingBooking={api.stateVo.hasUpcomingBooking}
                primaries={api.primaryInsurances}
                secondaries={api.secondaryInsurances}
                defaultId={api.defaultInsurance?.id}
                selectedId={api.checkInsurance?.id}
                onSelect={api.selectInsurance}
                onCheck={() => startCheck()}
            />
        );
    } else if (templateGap) {
        bodyNode = null;
    } else {
        bodyNode = (
            <IdleCard
                persona={persona}
                hasUpcomingBooking={api.stateVo.hasUpcomingBooking}
                onCheck={() => startCheck()}
            />
        );
    }

    // ── 卡上方的提示条（原型渲染顺序：team request → practice gap → card update → outreach）──
    // 「患者上传了新卡」：只看结果卡那张——它被患者改动的时间晚于这次 check 才提示（Re-check 正好查它）。
    // 刚查完、详情还没重拉时结果卡就是现在，自然不提示
    const cardVo = api.cardInsuranceId != null ? api.checkCards.get(api.cardInsuranceId) : undefined;
    const cardUpdatedAt = withZone(cardVo?.insuranceUpdatedAt || undefined);
    const showCardUpdate = !!latest && !!cardUpdatedAt && new Date(cardUpdatedAt) > new Date(latest.checkedAt);
    // 每条提示条的显示条件取名，渲染处直接复用
    const teamRequestNote = role === 'owner' && teamPendingProviderIds.length > 0;
    const npiNote = role === 'member' && needNpi;
    const templateNote = templateGap && !!templateMissing && !npiNote;
    const memberPayerNote = memberPayerGap && !!payerMissing;
    const ownerPayerNote = ownerPayerGap && !!payerMissing;
    const notes = (
        <>
            {teamRequestNote && (
                <TeamRequestBanner
                    count={teamPendingProviderIds.length}
                    onOpen={() => {
                        // 锚定到第一条未读通知的 provider chip；其余待办由 chip 圆点指明
                        setReviewAnchorProviderId(teamPendingProviderIds[0]);
                        setSetupModal('review');
                    }}
                />
            )}
            {npiNote && <PracticeGapNote variant="npi" />}
            {templateNote && templateMissing && (
                <TemplateGapNote
                    carrier={templateMissing.carrier || api.carrier}
                    canAdd={canAddToTemplate}
                    onAdd={openServiceTemplate}
                />
            )}
            {memberPayerNote && payerMissing && (
                <PracticeGapNote
                    variant="payer"
                    carrier={payerMissing.carrier}
                    state={payerMissing.state}
                    onMessageOwner={() => setMsgOwnerOpen(true)}
                />
            )}
            {ownerPayerNote && payerMissing && (
                <PracticeGapNote
                    variant="payer"
                    canSetUp
                    carrier={payerMissing.carrier}
                    state={payerMissing.state}
                    onSetUp={() => {
                        // 锚定 self + 本患者卡住的 carrier/state（blocking prop 保证该格子存在）
                        setReviewAnchorProviderId(undefined);
                        setSetupModal('review');
                    }}
                />
            )}
            {showCardUpdate && <CardUpdateNote cardUpdatedAt={cardUpdatedAt!} />}
        </>
    );

    return (
        // 10/21：模块挪到 Insurance 标题上方（Edit 按钮下），一律与上下留 16px——不再有「贴住标题」的 idle 特例
        <div className={s.module}>
            {notes}
            {bodyNode}
            <CostConfirmModal
                open={!!costModal}
                mode={costModal || 'check'}
                // 点名这次实际发往的那张（Re-check / 首查 / 查另一张）；advanced 搜的是匹配不上的那张，沿用结果卡保司
                carrier={
                    costModal === 'advanced'
                        ? api.carrier
                        : (api.activeInsurances || []).find((ins) => ins.id === costTargetId)?.carrier || api.carrier
                }
                // 「查另一张」选的是次保险：正文说 secondary 而不是 other（原型 10/21）
                targetIsSecondary={api.secondaryInsurances.some((ins) => ins.id === costTargetId)}
                monthToDateCents={api.stateVo.monthToDateCents}
                priceCents={costModal === 'advanced' ? ELIGIBILITY_ADVANCED_PRICE_CENTS : ELIGIBILITY_PRICE_CENTS}
                onCancel={() => setCostModal(null)}
                onConfirm={confirmCost}
                // 批钱之后还有设置步骤时按钮说 Next（member 的点击终点是 toast，不算步骤，仍说 Run check）
                runLabel={needsSetup && role !== 'member' ? NPI_MODAL_COPY.next : undefined}
                practiceView={!!api.isGroupPractice}
            />
            <NpiSetupModal
                open={!!setupModal}
                mode={setupModal || 'review'}
                role={role}
                blocking={payerMissing}
                // blocking 格子归谁：practice 视图是归属 provider（precheck override 查的是他的
                // payer 配置），my/solo 是自己——锚定与缺行插入都以此为准
                blockingProviderId={api.overrideProviderId}
                needNpi={needNpi}
                reviewAnchorProviderId={reviewAnchorProviderId}
                pendingProviderIds={teamPendingProviderIds}
                // 后端 precheck 只回 profileNpi 号码；radio 行的名字用当前登录 provider 补
                profileNpi={
                    setup?.profileNpi && {
                        ...setup.profileNpi,
                        name:
                            setup.profileNpi.name ||
                            `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
                    }
                }
                onCancel={() => setSetupModal(null)}
                onComplete={handleSetupComplete}
            />
            <MessageOwnerModal
                open={msgOwnerOpen}
                draft={payerMissing ? MSG_OWNER_COPY.draft(payerMissing.carrier, payerMissing.state) : ''}
                onClose={() => setMsgOwnerOpen(false)}
            />
            {cardRequestModal}
            {historyModal}
            <RouteConfirmModal
                open={!!api.routePick}
                picked={api.routePick}
                carrierOnFile={api.payerUnmatched?.carrierOnFile || api.carrier}
                draft={api.routePick ? buildRouteConfirmDraft(patientFirst, api.routePick, memberIdOnFile) : ''}
                sending={routeSending}
                onSend={handleRouteSend}
                onClose={api.cancelRoutePick}
            />
            <NewMessagePanel
                open={msgOpen}
                onClose={() => setMsgOpen(false)}
                prefillMessage={msgDraft}
                fixedRecipient={{
                    label: patientName || 'Patient',
                    prospectId: prospect.id,
                    identityId: api.isGroupPractice ? prospect.identityId : undefined,
                }}
            />
        </div>
    );
};

type Props = {
    api: TEligibilityApi;
};

// persona 只影响 CTA / 计费展示 / re-check，不影响 header tag（tag 在 hook 里算，gate 外消费）。
// insurance billing 白名单已移除（main cdff7ce8b），/status 对所有人开放，直接按 enrolled 分流。
// practice 视图按归属 provider 的 enrollment 判（check 计费落他头上，免不免费看他）——status 带 override
const EligibilityModule: React.FC<Props> = ({ api }) => {
    const [status] = useInsuranceBillingStatus(api.overrideProviderId);
    if (!api.enabled) {
        return null;
    }
    const enrolled = status?.canBind ? !!status?.bound && status?.onboardingCompleted === true : !!status?.canView;
    return <EligibilityModuleInner api={api} persona={enrolled ? 'managed' : 'manual'} />;
};

export default EligibilityModule;
